type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  error?: Error;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private isTest = process.env.NODE_ENV === 'test';
  
  private formatMessage(entry: LogEntry): string {
    const contextStr = entry.context ? ` ${JSON.stringify(entry.context)}` : '';
    return `[${entry.timestamp}] [${entry.level.toUpperCase()}] ${entry.message}${contextStr}`;
  }

  private log(level: LogLevel, message: string, context?: Record<string, unknown> | Error) {
    // Don't log in test environment unless it's an error
    if (this.isTest && level !== 'error') {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
    };

    if (context instanceof Error) {
      entry.error = context;
      entry.context = {
        errorMessage: context.message,
        errorStack: this.isDevelopment ? context.stack : undefined,
      };
    } else if (context) {
      entry.context = context;
    }

    // In production, we would send this to a logging service
    // For now, we'll use console methods appropriately
    if (this.isDevelopment) {
      const formattedMessage = this.formatMessage(entry);
      
      switch (level) {
        case 'debug':
          // Only log debug in development
          // eslint-disable-next-line no-console
          console.debug(formattedMessage);
          break;
        case 'info':
          // eslint-disable-next-line no-console
          console.info(formattedMessage);
          break;
        case 'warn':
          // eslint-disable-next-line no-console
          console.warn(formattedMessage);
          break;
        case 'error':
          // eslint-disable-next-line no-console
          console.error(formattedMessage, entry.error || '');
          break;
      }
    } else {
      // In production, we'd send to a logging service
      // For now, only log warnings and errors
      if (level === 'warn' || level === 'error') {
        // Remove sensitive data before logging
        const sanitizedEntry = this.sanitizeLogEntry(entry);
        // eslint-disable-next-line no-console
        console[level](JSON.stringify(sanitizedEntry));
      }
    }
  }

  private sanitizeLogEntry(entry: LogEntry): LogEntry {
    const sanitized = { ...entry };
    
    // Remove any potential sensitive data from context
    if (sanitized.context) {
      const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'authorization'];
      
      sanitized.context = Object.entries(sanitized.context).reduce((acc, [key, value]) => {
        const lowerKey = key.toLowerCase();
        const isSensitive = sensitiveKeys.some(sensitive => lowerKey.includes(sensitive));
        
        return {
          ...acc,
          [key]: isSensitive ? '[REDACTED]' : value,
        };
      }, {});
    }
    
    return sanitized;
  }

  debug(message: string, context?: Record<string, unknown>) {
    this.log('debug', message, context);
  }

  info(message: string, context?: Record<string, unknown>) {
    this.log('info', message, context);
  }

  warn(message: string, context?: Record<string, unknown>) {
    this.log('warn', message, context);
  }

  error(message: string, error?: Error | Record<string, unknown>) {
    this.log('error', message, error);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger };